/**
 * Location Detection for California Visitors
 * Uses ipapi.co for IP geolocation
 */

(function() {
  'use strict';
  
  // Configuration
  const CONFIG = {
    targetState: 'CA',
    cacheKey: 'visitor_location',
    cacheTimeKey: 'visitor_location_timestamp',
    cacheDuration: 24 * 60 * 60 * 1000, // 24 hours
    apiURL: 'https://ipapi.co/json/?key=FvRA55s6aw9PZtCIxWNO7TJ4v4cfTTNm2lsfm9Q0dPtBV6wgCH',
    maxRetries: 3,
    retryDelay: 1000, // 1 second
    requestTimeout: 5000 // 5 seconds
  };
  
  /**
   * Get cached location if available and not expired
   */
  function getCachedLocation() {
    try {
      const cached = localStorage.getItem(CONFIG.cacheKey);
      const cachedTime = localStorage.getItem(CONFIG.cacheTimeKey);
      
      if (cached && cachedTime) {
        const age = Date.now() - parseInt(cachedTime, 10);
        
        if (age < CONFIG.cacheDuration) {
          return JSON.parse(cached);
        }
      }
    } catch (error) {
      console.error('Cache read error:', error);
    }
    
    return null;
  }
  
  /**
   * Save location to cache
   */
  function cacheLocation(locationData) {
    try {
      localStorage.setItem(CONFIG.cacheKey, JSON.stringify(locationData));
      localStorage.setItem(CONFIG.cacheTimeKey, Date.now().toString());
    } catch (error) {
      console.error('Cache write error:', error);
    }
  }
  
  /**
   * Sleep utility for retry delays
   */
  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  /**
   * Fetch with timeout
   */
  async function fetchWithTimeout(url, options, timeout) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }
  
  /**
   * Fetch location from ipapi.co with retry logic
   */
  async function fetchLocation() {
    let lastError;
    
    for (let attempt = 1; attempt <= CONFIG.maxRetries; attempt++) {
      try {
        console.log(`🔄 API Request (Attempt ${attempt}/${CONFIG.maxRetries})...`);
        
        const response = await fetchWithTimeout(
          CONFIG.apiURL,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json'
            }
          },
          CONFIG.requestTimeout
        );
        
        if (!response.ok) {
          // Handle specific HTTP errors
          if (response.status === 429) {
            throw new Error(`Rate limit exceeded (HTTP 429)`);
          } else if (response.status >= 500) {
            throw new Error(`Server error (HTTP ${response.status})`);
          } else {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
        }
        
        const data = await response.json();
        
        // Validate response has required data
        if (!data.country_code || !data.region_code) {
          throw new Error('Invalid API response: missing required fields');
        }
        
        console.log('✅ API request successful');
        
        // Normalize data structure
        return {
          ip: data.ip,
          country: data.country_code,
          countryName: data.country_name,
          state: data.region_code,
          stateName: data.region,
          city: data.city,
          postal: data.postal,
          timezone: data.timezone,
          latitude: data.latitude,
          longitude: data.longitude
        };
        
      } catch (error) {
        lastError = error;
        
        // Check if it's a timeout error
        if (error.name === 'AbortError') {
          console.warn(`⏱️  Request timeout (attempt ${attempt})`);
        } else {
          console.warn(`⚠️  Request failed (attempt ${attempt}): ${error.message}`);
        }
        
        // Don't retry on rate limit errors (will fail anyway)
        if (error.message.includes('Rate limit')) {
          console.error('❌ Rate limit hit - stopping retries');
          throw error;
        }
        
        // Wait before retrying (exponential backoff)
        if (attempt < CONFIG.maxRetries) {
          const delay = CONFIG.retryDelay * Math.pow(2, attempt - 1);
          console.log(`⏳ Retrying in ${delay}ms...`);
          await sleep(delay);
        }
      }
    }
    
    // All retries failed
    throw new Error(`Failed after ${CONFIG.maxRetries} attempts: ${lastError.message}`);
  }
  
  /**
   * Check if visitor is from California
   */
  function isCaliforniaVisitor(locationData) {
    if (!locationData || !locationData.state) {
      return false;
    }
    
    return locationData.state === CONFIG.targetState;
  }
  
  /**
   * Show California-specific content
   */
  function showCaliforniaContent() {
    // Find elements with data-california attribute
    const californiaElements = document.querySelectorAll('[data-show-california]');
    
    californiaElements.forEach((element) => {
      element.style.display = 'block';
    });
    
    // Dispatch custom event for other scripts to listen to
    const event = new CustomEvent('californiaVisitorDetected', {
      detail: { state: 'CA' }
    });
    document.dispatchEvent(event);
  }
  
  /**
   * Main detection function
   */
  async function detectLocation() {
    console.log('🌎 Detecting location...');
    console.log('⚠️  CACHING TEMPORARILY DISABLED FOR TESTING');
    
    try {
      // CACHING DISABLED FOR TESTING - Always fetch fresh location
      /* 
      // Check cache first
      let locationData = getCachedLocation();
      
      if (!locationData) {
        locationData = await fetchLocation();
        cacheLocation(locationData);
      }
      */
      
      // Always fetch fresh location (no cache)
      let locationData = await fetchLocation();
      
      // Log location details
      console.log('📍 Location:', locationData.city, locationData.stateName, locationData.country);
      console.log('🗺️  Full Details:', {
        state: locationData.state,
        stateName: locationData.stateName,
        city: locationData.city,
        postal: locationData.postal,
        ip: locationData.ip
      });
      
      // Check if California visitor
      const isCA = isCaliforniaVisitor(locationData);
      console.log('🎯 California Status:', isCA ? '✅ YES - Visitor is from California' : '❌ NO - Not from California');
      
      if (isCA) {
        showCaliforniaContent();
      }
      
      // Make location data globally available
      window.visitorLocation = locationData;
      
    } catch (error) {
      console.error('❌ Location detection failed:', error.message);
      console.log('ℹ️  California content will remain hidden (safe default)');
      
      // Store failure state to avoid repeated attempts
      window.visitorLocation = { error: true, message: error.message };
    }
  }
  
  /**
   * Initialize when DOM is ready
   */
  function init() {
    detectLocation();
  }
  
  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Utility: Clear cache (for testing)
  window.clearLocationCache = function() {
    localStorage.removeItem(CONFIG.cacheKey);
    localStorage.removeItem(CONFIG.cacheTimeKey);
    console.log('✅ Cache cleared! Reload to fetch fresh data.');
  };
  
})();
